import React, { useState, useEffect } from 'react';
import FormContainer from '../components/FormContainer';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../components/Loader';
import ForgotPasswordModal from '../components/User/ForgotPasswordModal';
import VerifyEmailReminderModal from '../components/User/VerifyEmailReminderModal';

import {
  useLoginMutation,
  useGoogleLoginMutation,
  useGetUserProfileQuery,
} from '../slices/usersApiSlices';
import { setCredentials, logoutUser } from '../slices/authSlice';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';



//loads info from the front end .env
const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || '';



const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('')

  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const [showVerifyModal, setShowVerifyModal] = useState(false)

  const [login, { isLoading }] = useLoginMutation();
  const [googleLogin] = useGoogleLoginMutation();
  const { isLoading: isProfileLoading, isError: isProfileError } = useGetUserProfileQuery(undefined, {
    skip: !userInfo,
    refetchOnMountOrArgChange: true,
  });

  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const redirect = sp.get('redirect') || '/SquadDataScreen';

  // redirect user if already loged in
  useEffect(() => {
    if (userInfo && !isProfileLoading && !isProfileError) {
      navigate(redirect);
    }

    if (userInfo && isProfileError) {
      dispatch(logoutUser());
    }
  }, [userInfo, isProfileLoading, isProfileError, redirect, navigate, dispatch]);

  const submitHandler = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    try {
      const res = await login({ email: normalizedEmail, password }).unwrap();
      if (!res.emailVerified){
        setEmail(normalizedEmail)
        setShowVerifyModal(true)
        return
      }
      dispatch(setCredentials(res));
      navigate(redirect);
    } catch (error) {
      //toast.error(error?.data?.message || error.error, {autoClose: 1500, position: "bottom-right"});
      setErrorMessage(error?.data?.message || error.error)
    }
  };

  const handleGoogleSuccess = async (response) => {
    try {
      const decoded = jwtDecode(response.credential);// Decode JWT token from Google
      const { email, name, sub } = decoded;// Extract user details


      const res = await googleLogin({ email, name, googleId: sub }).unwrap();
      dispatch(setCredentials(res));
      navigate(redirect);
    } catch (error) {
      setErrorMessage("Google login failed. Please try again.")
      //toast.error("Google login failed. Please try again.", {autoClose: 2500});
    }
  };

  const handleGoogleFailure = () => {
    setErrorMessage("Google login failed. Please try again.")
    //toast.error("Google login failed. Please try again.", {autoClose: 2500});
  };

  const content = (
    <>
    <title>Login | ShotArc</title>
    <FormContainer>
      <div className="auth-page-wrap">
      <div className="auth-panel">
        <h1 className="auth-title">Sign In</h1>
        <p className="auth-subtitle">Welcome back. Continue your analysis workflow.</p>
        <Form onSubmit={submitHandler}>
          <Form.Group controlId="email" className="my-3">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              required
            />
          </Form.Group>

          <Form.Group controlId="password" className="my-3">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Form.Group>

          <p className="text-danger">{errorMessage || ""}</p>

          <Button type="submit" variant="warning" className="mt-2 w-100 fw-semibold auth-submit-btn" disabled={isLoading}>
            Sign In
          </Button>

          {isLoading && <Loader />}
        </Form>

        {/* Google Login Button */}
        {clientId ? (
          <div className="mt-3">
            
            <div className="auth-social-divider">
              <span>Or continue with Google</span>
            </div>
              
            <div className="auth-google-inline">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleFailure}
                theme="outline"
                size="large"
                text="signin_with"
                shape="pill"
                logo_alignment="left"
                width="380"
              />
            </div>
            
          </div>
        ) : (
          <p className="mt-3 text-danger mb-0">
            Google login is unavailable: missing REACT_APP_GOOGLE_CLIENT_ID.
          </p>
        )}
        <div className="auth-divider" />
        <div className="auth-assist-list">
          <div className="auth-assist-item">
            <p className="auth-helper-text mb-0">
              Don't have an account?
            </p>
            <Link
              to={redirect ? `/RegisterScreen?redirect=${redirect}` : '/RegisterScreen'}
              className="btn fw-semibold auth-link-btn auth-register-btn"
            >
              Register
            </Link>
          </div>

          <div className="auth-assist-item">
            <p className="auth-helper-text mb-0">
              Forgot Password?
            </p>
            <Button
              className="btn fw-semibold auth-link-btn auth-reset-btn"
              onClick={()=>{setShowForgotPasswordModal(true)}}
            >
              Reset Password
            </Button>
          </div>

          <div className="auth-assist-item">
            <p className="auth-helper-text mb-0">
              Learn More
            </p>
            <Link
              to="/WhatIsShotArc"
              className="btn fw-semibold auth-link-btn auth-info-btn"
            >
              What is ShotArc?
            </Link>
          </div>
        </div>
      </div>
      </div>
    </FormContainer>
    <ForgotPasswordModal 
      show={showForgotPasswordModal}
      onHide={()=>{setShowForgotPasswordModal(false)}}
    />

    <VerifyEmailReminderModal 
      show={showVerifyModal}
      onHide={()=>setShowVerifyModal(false)}
      email={email}
    />


    </>
  );

  if (!clientId) {
    return content;
  }

  return <GoogleOAuthProvider clientId={clientId}>{content}</GoogleOAuthProvider>;
};

export default LoginScreen;
